import { useEffect, useRef, useState } from 'react';
import type { Class } from '../data/types';
import { rebuildOnTemplateSwitch } from './defaults';
import { templatesForKind, getTemplate } from './templates';
import { PUBLISH_THEMES } from './themes';
import { PUBLISH_TEXT_LIMITS, type PublishConfig, type TemplateId, type WidgetId } from './types';
import { resolveWidgetOrder, reorderWidget } from './widgetOrder';
import { PUBLISH_SOUND_LIBRARY, playSoundRecipe } from './sounds';
import { suggestForLabel, type LabelSuggestion } from './labelSuggestions';
import './Composer.css';

/**
 * Composer — the left-side editor in the Publish view.
 *
 * Renders a compact accordion of editable surfaces (template, theme,
 * text, per-class flair). Pure controlled component: parent owns the
 * `PublishConfig` state and persistence; this component only emits
 * deltas.
 */

export interface ComposerProps {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  projectKind: 'image' | 'audio' | 'pose';
  projectName: string;
  onChange: (next: PublishConfig) => void;
}

const EMOJI_PALETTE = [
  '✨',
  '🎉',
  '🎈',
  '🌟',
  '🐶',
  '🐱',
  '🐼',
  '🦊',
  '🐝',
  '🍕',
  '🍩',
  '🍓',
  '🌈',
  '🚀',
  '⚡️',
  '🎯',
  '🎵',
  '👋',
  '👍',
  '✌️',
  '🤘',
  '🌸',
  '🔥',
  '💫',
];

interface WidgetDef {
  id: WidgetId;
  title: string;
  hint: string;
  icon: string;
  /** soundOnMatch has no visual stack position — kept in the toggle
   *  list, but excluded from up/down reordering. */
  orderable?: boolean;
}

/** Catalogue of toggleable widgets. The visual stacking order in the
 *  composer + published page is driven by `config.widgetOrder`, which
 *  the kid can rearrange with ↑/↓ buttons. soundOnMatch is audio-only,
 *  so it sits at the bottom of the toggle list and isn't reorderable. */
const WIDGET_DEFS: Record<WidgetId, WidgetDef> = {
  bigBanner: {
    id: 'bigBanner',
    icon: '📣',
    title: 'Big result banner',
    hint: 'A large "Looks like Dog 🐶" callout when the model is sure.',
    orderable: true,
  },
  confidenceBars: {
    id: 'confidenceBars',
    icon: '📊',
    title: 'Confidence bars',
    hint: 'Show how sure the model is about each class.',
    orderable: true,
  },
  scoreStreak: {
    id: 'scoreStreak',
    icon: '🏆',
    title: 'Score & streak',
    hint: 'Counts every match the model makes, plus the longest streak.',
    orderable: true,
  },
  historyStrip: {
    id: 'historyStrip',
    icon: '🎞️',
    title: 'Recent predictions',
    hint: 'A scrolling strip of the last few things the model spotted.',
    orderable: true,
  },
  qrCode: {
    id: 'qrCode',
    icon: '🔗',
    title: 'QR code',
    hint: '"Scan with a phone to try it" — great for showing off in person.',
    orderable: true,
  },
  shareLinks: {
    id: 'shareLinks',
    icon: '📤',
    title: 'Share buttons',
    hint: 'Copy link, send to a phone, or email it to grandma.',
    orderable: true,
  },
  soundOnMatch: {
    id: 'soundOnMatch',
    icon: '🔔',
    title: 'Sound on match',
    hint: 'Plays a friendly tone when the model fires.',
  },
};

export function Composer({
  config,
  classes,
  projectKind,
  projectName,
  onChange,
}: ComposerProps): JSX.Element {
  const template = getTemplate(config.template);
  const availableTemplates = templatesForKind(projectKind);

  const update = (patch: Partial<PublishConfig>): void => {
    onChange({ ...config, ...patch, version: config.version + 1 });
  };

  const updateText = (patch: Partial<PublishConfig['text']>): void => {
    onChange({
      ...config,
      text: { ...config.text, ...patch },
      version: config.version + 1,
    });
  };

  const updateClass = (
    classId: string,
    patch: Partial<PublishConfig['perClass'][string]>,
  ): void => {
    onChange({
      ...config,
      perClass: {
        ...config.perClass,
        [classId]: { ...(config.perClass[classId] ?? {}), ...patch },
      },
      version: config.version + 1,
    });
  };

  const switchTemplate = (id: TemplateId): void => {
    if (id === config.template) return;
    onChange(
      rebuildOnTemplateSwitch({
        template: id,
        classes,
        projectName,
        previous: config,
      }),
    );
  };

  return (
    <div className="bl-composer">
      <Section title="Pick a template">
        <div className="bl-composer__templates">
          {availableTemplates.map((t) => (
            <button
              type="button"
              key={t.id}
              className={`bl-composer__template${
                t.id === config.template ? ' bl-composer__template--selected' : ''
              }`}
              onClick={() => switchTemplate(t.id)}
              aria-pressed={t.id === config.template}
            >
              <span className="bl-composer__template-name">{t.name}</span>
              <span className="bl-composer__template-blurb">{t.blurb}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Theme">
        <div className="bl-composer__themes" role="radiogroup" aria-label="Theme">
          {PUBLISH_THEMES.map((theme) => (
            <ThemeChip
              key={theme.id}
              theme={theme}
              selected={theme.id === config.themeId}
              onSelect={() => update({ themeId: theme.id })}
            />
          ))}
        </div>
      </Section>

      {template.layoutVariants.length > 1 && (
        <Section title="Layout">
          <div className="bl-composer__layout-row" role="radiogroup">
            {template.layoutVariants.map((label, i) => (
              <button
                type="button"
                key={i}
                role="radio"
                aria-checked={config.layoutVariant === i}
                className={`bl-composer__layout-chip${
                  config.layoutVariant === i ? ' bl-composer__layout-chip--selected' : ''
                }`}
                onClick={() => update({ layoutVariant: i })}
              >
                {label}
              </button>
            ))}
          </div>
        </Section>
      )}

      <Section title="Words">
        <LimitedInput
          label="Title"
          value={config.text.title}
          maxLength={PUBLISH_TEXT_LIMITS.title}
          onChange={(v) => updateText({ title: v })}
        />
        <LimitedInput
          label="Subtitle"
          value={config.text.subtitle ?? ''}
          maxLength={PUBLISH_TEXT_LIMITS.subtitle}
          onChange={(v) => updateText({ subtitle: v || undefined })}
          multiline
        />
        {template.id === 'class-quiz' && (
          <LimitedInput
            label="Button label"
            value={config.text.actionLabel ?? ''}
            maxLength={PUBLISH_TEXT_LIMITS.actionLabel}
            onChange={(v) => updateText({ actionLabel: v || undefined })}
          />
        )}
      </Section>

      <Section title="Widgets">
        <p className="bl-composer__widgets-hint">
          Everything below is optional. Add what helps tell your story — leave the rest off so the
          page stays clean. Use the arrows to reorder how widgets stack on the page.
        </p>
        <WidgetList
          config={config}
          onToggle={(id, on) =>
            update({
              widgets: { ...(config.widgets ?? {}), [id]: on },
            })
          }
          onReorder={(id, dir) =>
            update({
              widgetOrder: reorderWidget(resolveWidgetOrder(config.widgetOrder), id, dir),
            })
          }
        />
      </Section>

      <Section title="Each class">
        <ul className="bl-composer__classes">
          {classes.map((cls) => {
            const cust = config.perClass[cls.id] ?? {};
            // Suggestions surface only after the kid types a display
            // label. Falling back to the class name pre-fills the
            // dropdown before the kid expresses any intent — too
            // noisy on first render.
            const labelForSuggestions = cust.label ?? '';
            return (
              <li key={cls.id} className="bl-composer__class">
                <p className="bl-composer__class-name">{cls.name}</p>
                <EmojiPicker
                  value={cust.emoji ?? '✨'}
                  onChange={(emoji) => updateClass(cls.id, { emoji })}
                />
                <LimitedInput
                  label="Display label"
                  value={cust.label ?? ''}
                  placeholder={cls.name}
                  maxLength={PUBLISH_TEXT_LIMITS.perClassLabel}
                  onChange={(v) => updateClass(cls.id, { label: v || undefined })}
                />
                <LabelSuggestions
                  text={labelForSuggestions}
                  soundEnabled={!!config.widgets?.soundOnMatch}
                  onApply={(s) =>
                    updateClass(cls.id, {
                      emoji: s.emoji,
                      ...(s.soundId ? { soundId: s.soundId } : {}),
                    })
                  }
                />
                <SoundPicker
                  value={cust.soundId}
                  enabled={!!config.widgets?.soundOnMatch}
                  onChange={(soundId) => updateClass(cls.id, { soundId: soundId || undefined })}
                />
              </li>
            );
          })}
        </ul>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="bl-composer__section">
      <h3 className="bl-composer__section-title">{title}</h3>
      {children}
    </section>
  );
}

function ThemeChip({
  theme,
  selected,
  onSelect,
}: {
  theme: (typeof PUBLISH_THEMES)[number];
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`bl-composer__theme${selected ? ' bl-composer__theme--selected' : ''}`}
      onClick={onSelect}
    >
      <span
        className="bl-composer__theme-swatch"
        style={{ background: theme.gradient ?? theme.bg }}
        aria-hidden="true"
      >
        <span className="bl-composer__theme-dot" style={{ background: theme.accent }} />
        <span className="bl-composer__theme-dot" style={{ background: theme.secondary }} />
      </span>
      <span className="bl-composer__theme-label">{theme.label}</span>
    </button>
  );
}

function LimitedInput({
  label,
  value,
  maxLength,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  maxLength: number;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}): JSX.Element {
  const id = useRef(
    `bl-comp-${label.toLowerCase().replace(/\s+/g, '-')}-${Math.random().toString(36).slice(2, 7)}`,
  ).current;
  return (
    <label className="bl-composer__field" htmlFor={id}>
      <span className="bl-composer__field-label">
        {label}
        <span className="bl-composer__field-counter">
          {value.length}/{maxLength}
        </span>
      </span>
      {multiline ? (
        <textarea
          id={id}
          rows={2}
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

/** Suggestion strip below the per-class label input. Computes
 *  matches from the curated catalogue against the typed label and
 *  shows up to four "tap to apply" chips. Each chip stamps the
 *  emoji + matching sound (when one exists) onto the class — but
 *  only the bits the kid hasn't already explicitly chosen. The
 *  sound piece is suppressed when `soundOnMatch` is off, so the kid
 *  doesn't end up with a soundId stuck on a class they can't preview. */
function LabelSuggestions({
  text,
  soundEnabled,
  onApply,
}: {
  text: string;
  soundEnabled: boolean;
  onApply: (s: LabelSuggestion) => void;
}): JSX.Element | null {
  const suggestions = suggestForLabel(text);
  if (suggestions.length === 0) return null;
  return (
    <ul className="bl-composer__suggestions" role="listbox" aria-label="Label suggestions">
      {suggestions.map((s) => (
        <li key={s.label}>
          <button
            type="button"
            role="option"
            aria-selected="false"
            className="bl-composer__suggestion"
            title={
              s.soundId && soundEnabled
                ? `Use ${s.emoji} + ${s.soundId} sound`
                : `Use ${s.emoji} for ${s.label}`
            }
            onClick={() =>
              onApply(
                soundEnabled
                  ? s
                  : // Strip soundId when sound-on-match is off so the
                    // kid doesn't get a hidden sound assignment.
                    ({ label: s.label, emoji: s.emoji } as LabelSuggestion),
              )
            }
          >
            <span aria-hidden="true">{s.emoji}</span>
            <span className="bl-composer__suggestion-label">{s.label}</span>
            {s.soundId && soundEnabled && (
              <span className="bl-composer__suggestion-sound" aria-label={`Plays ${s.soundId}`}>
                🔊
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Per-class sound picker. Shown under each class in the "Each class"
 *  panel. Disabled (greyed out, with a hint) when the master
 *  `Sound on match` widget is off — the kid still sees the slot, so
 *  the relationship is obvious. Hovering / tapping a sound previews
 *  it via the same Web Audio synth the published page uses. */
function SoundPicker({
  value,
  enabled,
  onChange,
}: {
  value: string | undefined;
  enabled: boolean;
  onChange: (soundId: string | undefined) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const current = PUBLISH_SOUND_LIBRARY.find((s) => s.id === value);
  const triggerLabel = current ? `${current.emoji} ${current.label}` : '🔊 Pick a sound';
  function preview(soundId: string | undefined): void {
    if (!soundId) return;
    const def = PUBLISH_SOUND_LIBRARY.find((s) => s.id === soundId);
    if (!def) return;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    if (!ctxRef.current) ctxRef.current = new Ctor();
    void ctxRef.current.resume();
    playSoundRecipe(ctxRef.current, def);
  }
  return (
    <div className="bl-composer__sound" ref={wrapRef}>
      <div
        className={`bl-composer__sound-trigger${
          enabled ? '' : ' bl-composer__sound-trigger--disabled'
        }`}
      >
        {current && (
          <button
            type="button"
            className="bl-composer__sound-play"
            onClick={(e) => {
              e.stopPropagation();
              if (enabled) preview(current.id);
            }}
            disabled={!enabled}
            aria-label={`Play ${current.label}`}
            title={enabled ? `Play ${current.label}` : 'Turn on "Sound on match" first'}
          >
            <span aria-hidden="true">▶</span>
          </button>
        )}
        <button
          type="button"
          className="bl-composer__sound-pick"
          onClick={() => enabled && setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-disabled={!enabled}
          disabled={!enabled}
          title={
            enabled
              ? 'Pick a sound for this class'
              : 'Turn on "Sound on match" to enable per-class sounds'
          }
        >
          <span>{triggerLabel}</span>
          <span aria-hidden="true" className="bl-composer__sound-caret">
            ▾
          </span>
        </button>
      </div>
      {open && (
        <ul className="bl-composer__sound-grid" role="listbox" aria-label="Sound clip">
          <li>
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className={`bl-composer__sound-option${
                !value ? ' bl-composer__sound-option--selected' : ''
              }`}
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              <span aria-hidden="true">🔇</span>
              <span className="bl-composer__sound-option-label">Silent</span>
              <span className="bl-composer__sound-option-hint">No sound for this class.</span>
            </button>
          </li>
          {PUBLISH_SOUND_LIBRARY.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                role="option"
                aria-selected={s.id === value}
                className={`bl-composer__sound-option${
                  s.id === value ? ' bl-composer__sound-option--selected' : ''
                }`}
                onClick={() => {
                  onChange(s.id);
                  preview(s.id);
                  setOpen(false);
                }}
                onMouseEnter={() => preview(s.id)}
              >
                <span aria-hidden="true">{s.emoji}</span>
                <span className="bl-composer__sound-option-label">{s.label}</span>
                <span className="bl-composer__sound-option-hint">{s.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WidgetList({
  config,
  onToggle,
  onReorder,
}: {
  config: PublishConfig;
  onToggle: (id: WidgetId, on: boolean) => void;
  onReorder: (id: WidgetId, dir: 'up' | 'down') => void;
}): JSX.Element {
  // Resolve the current visual order. Orderable widgets sit on top
  // (in kid-chosen order); audio-only sound-on-match anchors below.
  const resolved = resolveWidgetOrder(config.widgetOrder);
  const orderable = resolved.filter((id) => WIDGET_DEFS[id]?.orderable);
  const trailing: WidgetId[] = (Object.keys(WIDGET_DEFS) as WidgetId[]).filter(
    (id) => !WIDGET_DEFS[id].orderable,
  );
  const rows: WidgetId[] = [...orderable, ...trailing];
  return (
    <ul className="bl-composer__widgets">
      {rows.map((id, i) => {
        const def = WIDGET_DEFS[id];
        if (!def) return null;
        const checked = !!config.widgets?.[id];
        const canMoveUp = !!def.orderable && i > 0;
        const canMoveDown = !!def.orderable && i < orderable.length - 1;
        return (
          <li key={id} className="bl-composer__widgets-row">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- label wraps both checkbox + nested-span title; lint rule misses the nested text node */}
            <label
              className={`bl-composer__toggle${checked ? ' bl-composer__toggle--checked' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onToggle(id, e.target.checked)}
              />
              <span className="bl-composer__toggle-copy">
                <span className="bl-composer__toggle-title">
                  {def.icon} {def.title}
                </span>
                <span className="bl-composer__toggle-hint">{def.hint}</span>
              </span>
            </label>
            {def.orderable && (
              <span className="bl-composer__widgets-reorder">
                <button
                  type="button"
                  className="bl-composer__widgets-arrow"
                  onClick={() => onReorder(id, 'up')}
                  disabled={!canMoveUp}
                  aria-label={`Move ${def.title} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="bl-composer__widgets-arrow"
                  onClick={() => onReorder(id, 'down')}
                  disabled={!canMoveDown}
                  aria-label={`Move ${def.title} down`}
                >
                  ↓
                </button>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function EmojiPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (emoji: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  // Close on outside click. Anchored to the trigger button.
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="bl-composer__emoji" ref={wrapRef}>
      <button
        type="button"
        className="bl-composer__emoji-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Pick emoji"
      >
        {value}
      </button>
      {open && (
        <div className="bl-composer__emoji-grid" role="listbox">
          {EMOJI_PALETTE.map((e) => (
            <button
              type="button"
              key={e}
              role="option"
              aria-selected={e === value}
              className="bl-composer__emoji-option"
              onClick={() => {
                onChange(e);
                setOpen(false);
              }}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
